import React from 'react';
import { Text } from '@belong/ui';
import {
  MaintenanceModel,
  MaintenancePaymentModel,
  MaintenancePriceRangeAggregateModel,
} from 'api/models';
import classNames from 'classnames/bind';
import Money from 'components/Money/Money';
import { MAINTENANCE_TASK_MOBILE_PROPS } from 'containercomponents/MaintenanceTaskListContainerComponent/MaintenanceTasks/MaintenanceProps';
import { isNil, isNumber } from 'es-toolkit/compat';
import styles from './MaintenanceTaskCost.module.css';

const cx = classNames.bind(styles);

type MaintenanceTaskCostProps = {
  // Both may arrive `undefined` — the task list leaves them unassigned on the
  // paths that compute neither — and the body tests for that with `isNil`.
  estimatedCost:
    | ''
    | 'Pricing TBD'
    | MaintenancePaymentModel['estimatedAdjustmentApplied']
    | undefined;
  // Defaults to `null` below, and the body tests it with `isNil`.
  actualCost:
    | ''
    | 'Free'
    | MaintenancePaymentModel['actualAdjustmentApplied']
    | null
    | undefined;
  mobileDisplay?: string;
  maintenanceTaskStatus?: string;
  priceRange?: MaintenancePriceRangeAggregateModel;
  numberOfOptions?: number;
  requestCategory?: MaintenanceModel['requestCategory'];
  requestType?: MaintenanceModel['requestType'];
};

const useMaintenanceCost = (
  estimatedCost: MaintenanceTaskCostProps['estimatedCost'],
  actualCost: MaintenanceTaskCostProps['actualCost'],
  maintenanceTaskStatus: MaintenanceTaskCostProps['maintenanceTaskStatus'],
  requestCategory?: MaintenanceModel['requestCategory'],
  requestType?: MaintenanceModel['requestType'],
  priceRange?: MaintenancePriceRangeAggregateModel,
  numberOfOptions?: number,
) => {
  const CostTBD = (
    <span>
      Pricing: <span className="mr-xs font-semibold">TBD</span>
    </span>
  );

  if (actualCost === 'Free') {
    return 'Free';
  }

  if (isNumber(actualCost) && actualCost > 0) {
    return (
      <div>
        Pricing: <Money cash={actualCost} />
      </div>
    );
  }

  if (
    requestCategory === 'InHomeRepairs' &&
    requestType === 'Group' &&
    priceRange
  ) {
    let groupInHomeRepairsCost = CostTBD;
    if (priceRange.upperBoundTotal > 0) {
      if (priceRange.lowerBoundTotal < priceRange.upperBoundTotal) {
        groupInHomeRepairsCost = (
          <span>
            Est.{' '}
            <span className="mr-xs font-semibold">
              <Money cash={priceRange.lowerBoundTotal} /> -{' '}
              <Money cash={priceRange.upperBoundTotal} />
            </span>
          </span>
        );
      } else {
        groupInHomeRepairsCost = (
          <span>
            Est.{' '}
            <span className="mr-xs font-semibold">
              <Money cash={priceRange.lowerBoundTotal} />
            </span>
          </span>
        );
      }
    }

    return (
      <div>
        {groupInHomeRepairsCost}
        {numberOfOptions} Options Available
      </div>
    );
  }

  if (!isNil(estimatedCost)) {
    if (isNumber(estimatedCost) && estimatedCost > 0) {
      return (
        <div>
          Est.{' '}
          <span className="mr-xs font-semibold">
            <Money cash={estimatedCost} />
          </span>
        </div>
      );
    }

    if (maintenanceTaskStatus === 'Closed') return '';
  }

  return CostTBD;
};

const MaintenanceTaskCost = ({
  estimatedCost,
  actualCost = null,
  mobileDisplay = MAINTENANCE_TASK_MOBILE_PROPS.NO_MOBILE,
  maintenanceTaskStatus,
  priceRange,
  numberOfOptions,
  requestCategory,
  requestType,
}: MaintenanceTaskCostProps) => {
  const cost: React.ReactNode = useMaintenanceCost(
    estimatedCost,
    actualCost,
    maintenanceTaskStatus,
    requestCategory,
    requestType,
    priceRange,
    numberOfOptions,
  );

  return (
    <div className={cx('maintenanceTaskCost', mobileDisplay)}>
      <Text variant="p1">{cost}</Text>
    </div>
  );
};

export default MaintenanceTaskCost;
